import React, { useMemo, useRef } from 'react';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import {
  showTimeByDateType,
  newMathFormat,
  getCountryNameByCountryCode,
} from '@/pages/intelligence/utils/commonUtils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import moment from 'moment';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData = [], // 折线 数据
    isNodata,
    imageType,
    metric,
    titleNow,
    chartLoading,
    setChartLoading,
    regionsCountries,
    renderType,
    tableData,
    gameName,
  } = props;

  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const allTableData = useMemo(() => {
    return tableData?.find((item: any) => item.market == 'Total') || {};
  }, [tableData]);

  // const allLineData = useMemo(() => {
  //   return lineData?.find((item: any) => item.name == 'All Games') || {};
  // }, [lineData]);

  const normalTableList = useMemo(() => {
    return tableData?.filter((item: any) => item.market != 'Total') || [];
  }, [tableData]);

  const normalLineList = useMemo(() => {
    return lineData?.filter((item: any) => item.name != 'Total') || [];
  }, [lineData]);

  const isPercentLine = useMemo(() => {
    switch (renderType) {
      case RenderType.GROWTH_ONLY:
        return true;
      default:
        return false;
    }
  }, [tableData, renderType]);

  const barNameList = useMemo(() => {
    const tempListTemp =
      normalTableList?.map((item: any) => {
        const nameStr = item?.market || item?.name || '';
        return nameStr;
      }) || [];
    return [...tempListTemp, 'others'];
  }, [tableData]);

  const barSeriesData = useMemo(() => {
    const brList: any = [];
    let allNum = 0;
    let sum = 0;
    const labelObj = {
      show: true,
      position: 'top',
      color: '#444',
      distance: 4,
      fontSize: 12,
      formatter: (params: any) =>
        `${
          newMathFormat(Number(params.value), true) +
          (getIfPercentage(metric) ? '%' : '')
        }`,
    };
    const itemObj =
      Array.isArray(allTableData.markets_metrics) &&
      allTableData.markets_metrics?.length > 0
        ? allTableData.markets_metrics[0]
        : [];
    allNum =
      itemObj?.metric?.find((subItem: any) => subItem.metric == metric)
        ?.value || 0;

    normalTableList?.forEach((item: any, i: any) => {
      const itemObj =
        Array.isArray(item.markets_metrics) && item.markets_metrics?.length > 0
          ? item.markets_metrics[0]
          : [];
      const barNum =
        itemObj?.metric?.find((subItem: any) => subItem.metric == metric)
          ?.value || 0;

      const objTemp = {
        type: 'bar',
        // name: getCountryNameByCountryCode(regionsCountries, item?.market),
        name: item?.market || item?.name || '',
        value: barNum,
        cover: item?.market,
        itemStyle: {
          color: ECHARTSCOLOR[i],
        },
        label: labelObj,
      };
      sum += barNum;

      brList.push(objTemp);
    });

    const objOthers = {
      type: 'bar',
      name: 'others',
      unifiedId: 'others',
      value: allNum - sum,
      cover: 'Others',
      itemStyle: {
        color: ECHARTSCOLOR[tableData?.length * 1 - 1],
      },
      label: labelObj,
    };
    return [...brList, objOthers];
  }, [tableData, metric]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    barOrPieData: barSeriesData,
    setChartLoading,
    // titleNow: titleNow,
    barNameList,
    topChartsType: 'pcconsole-country-bar',
    cardWidth: cardRef?.current?.clientWidth,
    chartType,
    ifNeedSourceImg: false,
    secondTitleRight: titleNow,
    startDate,
    endDate,
  });
  const pieSeriesData = useMemo(() => {
    const piList: any = [];
    let allNum = 0;
    let sum = 0;

    tableData?.forEach((item: any, i: any) => {
      const itemObj =
        Array.isArray(item.markets_metrics) && item.markets_metrics?.length > 0
          ? item.markets_metrics[0]
          : [];
      const metricNum =
        itemObj?.metric?.find((subItem: any) => subItem.metric == metric)
          ?.value || 0;
      if (item.market == 'Total') {
        allNum = metricNum;
      } else {
        const nameStr = item?.market;
        const objTemp = {
          name: getCountryNameByCountryCode(regionsCountries, nameStr),
          value: metricNum,
        };
        sum += metricNum;
        piList.push(objTemp);
      }
    });
    const objOthers = {
      name: 'Others',
      value: allNum - sum,
    };
    return [...piList, objOthers];
  }, [tableData, metric]);

  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    pieTitle: gameName,
    setChartLoading,
    chartLoading,
    chartType,
    chartsInstance,
    titleNow,
    startDate,
    endDate,
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];

    const listTemp = list.sort(
      (a: any, b: any) => moment(a.date) - moment(b.date),
    );

    const arr: any = [];
    for (const i in listTemp) {
      const metricNum = listTemp[i].value ? listTemp[i]?.value : null;
      arr.push({
        time: showTimeByDateType(
          listTemp[i].date,
          dateType?.toLocaleLowerCase(),
        ),
        value: isPercentLine ? metricNum * 100 : metricNum,
      });
    }
    return arr;
  };

  const getSumNum = (date: any, normalList: any) => {
    let sumNum = 0;
    for (const j in normalList) {
      if (
        !Array.isArray(normalList[j].history) ||
        normalList[j].history?.length < 0
      )
        return [];
      sumNum +=
        normalList[j]?.history?.find((item: any) => item.date == date)?.value ||
        0;
    }
    return sumNum;
  };

  const lineSeriesData = useMemo(() => {
    const arrGameData: any = [];
    for (const n in normalLineList) {
      const gameObj = normalLineList[n];
      const objTemp = {
        id: gameObj.id,
        name: gameObj?.name != '' ? gameObj?.name : gameObj?.region,
        history: getRpdTypeData(gameObj.history || []),
      };
      arrGameData.push(objTemp);
    }
    // arrGameData = [
    //   ...arrGameData,
    //   {
    //     id: 'Others',
    //     name: 'Others',
    //     history: getOthersData(allLineData.history || [], normalLineList),
    //   }
    // ]
    const temp = {
      source: Array.isArray(arrGameData) ? arrGameData : [],
    };
    return temp;
  }, [lineData, metric]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: 'source',
    // topChartsType: 'pcconsole-stats-line',
    imageType,
    secondTitleRight: titleNow,
    chartType,
    setChartLoading,
    dateType,
    isPercentLine,
    ifNeedMoveZero: false,
  });

  return (
    <>
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={chartLoading}
          lineOption={lineOption}
          barOption={barOption}
          pieOption={pieOption}
          chartType={chartType}
          isNodata={isNodata}
        />
      </div>
    </>
  );
};

export default Visualis;
